import { useTheme } from "@/hooks/useTheme";
import { CustomRoute } from "@/interfaces";
import { cn, getContrastTextColor } from "@/lib/utils";
import { canView, canEdit, getCrmAccess } from "@/lib/permissionUtils";
import { PermissionsMap } from "@/lib/permissions";
import { getCurrentUserRouter } from "@/routers/CurrentRoutes";
import { useUserStore } from "@/stores/userStore";
import { checkIsApprover, checkIsApproverforFinance } from "@/services/billApprovalService";
import {
  checkIsApprover as checkIsCommissionApprover,
  checkIsApproverforFinance as checkIsCommissionApproverForFinance,
} from "@/services/commissionApprovalService";
import { useGetUserDashboards, useGetAllDashboards } from "@/services/powerBiService";
import { ChevronDown } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Link, matchPath, useLocation } from "react-router";

// Segment-aware prefix match, so "/oms/orders" also matches "/oms/orders/create" and "/oms/orders/:id/edit"
// but "/commission" doesn't match "/commission-disputes". "/" only matches itself.
const matches = (path: string, pathname: string) =>
  !!path && (path === "/" ? pathname === "/" : !!matchPath({ path, end: false }, pathname));

// Most specific (longest) matching route, so a nested route wins over its prefix sibling.
const bestMatch = (items: CustomRoute[], pathname: string) =>
  items
    .filter((item) => matches(item.path, pathname))
    .reduce<CustomRoute | undefined>((best, item) => (!best || item.path.length > best.path.length ? item : best), undefined);

// Parents whose submenu (visible or hidden detail/edit routes) contains the current page.
const getOpenSections = (routes: CustomRoute[], pathname: string): Record<string, boolean> =>
  Object.fromEntries(
    routes.filter((r) => r.submenu?.some((s) => matches(s.path, pathname))).map((r) => [r.menuName, true]),
  );

// Left accent bar for the selected row (colour follows the sidebar text colour, so it contrasts on any theme).
const ACCENT_BAR =
  "relative before:absolute before:top-1.5 before:bottom-1.5 before:w-[3px] before:rounded-full before:bg-current";

const UserMenu = () => {
  const role = useUserStore((state) => state.getRole());
  const permissions = useUserStore((state) => state.user?.permissions) as PermissionsMap | undefined;
  const currentCompany = useUserStore((state) => state.currentCompany);

  // Memoize routes to prevent infinite re-renders
  const user = useUserStore((state) => state.user);
  const routes = useMemo(() => getCurrentUserRouter(role, user), [role, user]);
  const pathname = useLocation().pathname;
  // Lazy init so the section holding the current page is already open on first paint.
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>(() => getOpenSections(routes, pathname));
  const { theme } = useTheme();
  const textColor = getContrastTextColor(theme.secondaryColor);
  const isNetSuiteCompany = ((currentCompany?.invoicingAccounting || "").toString().trim().toLowerCase() === "netsuite");

  // Finance users have no single currentCompany (App.tsx nulls it out), so company-name based menu
  // gating (the WebiMax-specific items below) must fall back to the user's first company — otherwise
  // those checks always see an undefined name and misfire for Finance. Mirrors the useTheme fallback.
  const effectiveCompanyName = currentCompany?.companyName ?? user?.companies?.[0]?.companyName;

  // Finance users also have no currentCompany, so the OMS-active check (below) can't rely on
  // currentCompany?.isOmsActive either. Finance is assigned to multiple companies at once, so
  // show OMS if ANY assigned company has it active — hide it only when none do.
  const isOmsActiveForFinance = user?.companies?.some((c) => c.isOmsActive) ?? false;

  // Conditionally fetch dashboards to determine if the Power BI menu should be shown.
  // Non-Super users should only see the menu if they personally have a dashboard assigned
  // for the current company — not just because the company has dashboards assigned to others.
  // PowerBiAdmin manages dashboards across all companies (like Super) and has no currentCompany.
  const isPowerBiFullAdmin = role === "Super" || role === "PowerBiAdmin";
  const { data: dashboards, isLoading: dashboardsLoading } = isPowerBiFullAdmin
    ? useGetAllDashboards()
    : useGetUserDashboards(user?.id || "", currentCompany?.id || "");
  const hasDashboards = !dashboardsLoading && dashboards && dashboards.length > 0;

  const shouldHideBillingOption = (path?: string) => {
    if (!path) return false;

    // Hide Stripe Payment for Admin and Employee roles
    if (path === "/billing/stripe-payment" && (user?.role === "Admin" || user?.role === "Employee")) {
      return true;
    }

    // Features that should ONLY show for QuickBooks companies
    if (isNetSuiteCompany && (path.includes("recurring-invoices") || path === "/billing/stripe-payment")) {
      return true;
    }

    // Existing role-based hiding logic (only for non-NetSuite companies)
    if (!isNetSuiteCompany) {
      if (user?.role === "User" && (path === "/bills" || path === "/create-bill")) return true;
    }

    return false;
  };

  const shouldHideInvoicingOption = (menuName?: string, path?: string) => {
    if (!menuName) return false;

    // Hide "Ouickbooks menu" for Finance user if none of the company reuires QuickBooks
    if (
      (menuName === "QuickBooks") &&
      !user?.companies.some(obj => obj.invoicingAccounting === "QuickBooks") &&
      user?.role === "Finance"
    ) {
      return true;
    }

    // Hide "Netsuite menu" for Finance user if none of the company reuires NetSuite
    if (
      menuName === "NetSuite" &&
      !user?.companies.some(obj => obj.invoicingAccounting === "NetSuite") &&
      user?.role === "Finance"
    ) {
      return true;
    }


    if (
      (path === "/netsuite/approvals") &&
      currentCompany?.invoicingAccounting === "QuickBooks" &&
      user?.role === "Admin"
    ) {
      return true;
    }


    if (
      (path === "/quickbooks/approvals") &&
      currentCompany?.invoicingAccounting === "NetSuite" &&
      user?.role === "Admin"
    ) {
      return true;
    }

    return false;
  }

  // Check if current user is a bill approver — only relevant for Finance/Admin
  const [isApprover, setIsApprover] = useState<boolean | null>(null);
  useEffect(() => {
    if (user?.role === "Admin" || user?.role === "Employee") {
      checkIsApprover(currentCompany?.id)
        .then((res) => setIsApprover(res.isApprover))
        .catch(() => setIsApprover(false));
    } else if (user?.role === "Finance") {
      const assignedCompanies = user.companies.map((company) => company.id)
      if (assignedCompanies.length > 0) {
        checkIsApproverforFinance(assignedCompanies)
          .then((res) => setIsApprover(res.isApprover))
          .catch(() => setIsApprover(false));
      }
    }
  }, [user?.id, user?.role, currentCompany?.id]);

  // Same check for commission approvers (Super Admin → Commission Approvers, L1..L7). Drives the
  // "Commission Approvals" menu: shown under Commissions when that menu is visible to the user,
  // otherwise as its own top-level menu.
  const [isCommissionApprover, setIsCommissionApprover] = useState<boolean | null>(null);
  useEffect(() => {
    if (user?.role === "Admin" || user?.role === "Employee") {
      checkIsCommissionApprover(currentCompany?.id)
        .then((res) => setIsCommissionApprover(res.isApprover))
        .catch(() => setIsCommissionApprover(false));
    } else if (user?.role === "Finance") {
      const assignedCompanies = user.companies.map((company) => company.id);
      if (assignedCompanies.length > 0) {
        checkIsCommissionApproverForFinance(assignedCompanies)
          .then((res) => setIsCommissionApprover(res.isApprover))
          .catch(() => setIsCommissionApprover(false));
      }
    }
  }, [user?.id, user?.role, currentCompany?.id]);

  // Mirrors the top-level filter rules below for the "Commissions" menu (WebiMax-only, plus its
  // permissionKey for Employees).
  const commissionsRoute = routes.find((r) => r.menuName === "Commissions");
  const isCommissionsMenuVisible =
    !!commissionsRoute &&
    !commissionsRoute.hideInMenu &&
    effectiveCompanyName === "WebiMax" &&
    (!commissionsRoute.permissionKey || canView(permissions, commissionsRoute.permissionKey));

  // Keep the submenu open if the current pathname belongs to one of its children.
  // Otherwise close all submenus when navigating to a different top-level route.
  useEffect(() => {
    setOpenMenus(getOpenSections(routes, pathname));
  }, [pathname, routes]);

  const toggleMenu = (key: string) => {
    setOpenMenus((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Flat links: the most specific matching one is the selected link (keeps e.g. "Companies" lit on /companies/:id/edit).
  const activeFlatPath = bestMatch(
    routes.filter((r) => !r.submenu?.length && !r.hideInMenu),
    pathname,
  )?.path;

  return (
    <div className="space-y-2">
      {routes
        .filter((item: CustomRoute) => {
          // Don't show items marked as hideInMenu
          if (item.hideInMenu) return false;

          if (item.menuName === "Commission Approvals") {
            return isCommissionApprover === true && !isCommissionsMenuVisible;
          }
          if (shouldHideBillingOption(item.path)) return false;
          if (shouldHideInvoicingOption(item.menuName, item.path)) return false;

          if (effectiveCompanyName !== "WebiMax" && item.menuName === "Commissions") return false;
          if (effectiveCompanyName !== "WebiMax" && item.menuName === "Create Invoice") return false;

          // OMS visibility. Hidden for WebiMax entirely. Otherwise the company-level toggle
          // (isOmsActive) is authoritative: when OMS is disabled it is hidden for every portal role
          // except Admin (who manages the setting). Finance users have no currentCompany (App.tsx
          // nulls it out since they're assigned to many companies at once), so currentCompany?.isOmsActive
          // is always undefined for them and would hide OMS unconditionally — instead check across all
          // of their assigned companies and show OMS if any single one has it active.
          if (
            item.menuName === "OMS" &&
            (effectiveCompanyName === "WebiMax" ||
              (user?.role === "Finance"
                ? !isOmsActiveForFinance
                : !currentCompany?.isOmsActive && user?.role !== "Admin"))
          )
            return false;

          // Employees only get the OMS menu once an OMS permission is granted (Orders / Order
          // Approvals carry no permissionKey of their own, so without this they'd always show).
          if (
            item.menuName === "OMS" &&
            user?.role === "Employee" &&
            !canView(permissions, "oms") &&
            !canView(permissions, "oms_sales") &&
            !canView(permissions, "oms_operations")
          )
            return false;

          // CRM visibility: the company-level toggle (isCrmActive) is authoritative and
          // hidden for everyone (including Admin) when disabled, unlike OMS above.
          if (item.menuName === "CRM" && !currentCompany?.isCrmActive) {
            return false;
          }
          // Employees get CRM from their OMS permissions (see getCrmAccess), not a single permissionKey.
          if (item.menuName === "CRM" && user?.role === "Employee") {
            return getCrmAccess(user.role, permissions) !== "none";
          }

          // Hide Vendor Management for WebiMax (same as OMS). This must run before the permissionKey
          // check below, otherwise the check returns early and this hide never takes effect.
          if (item.menuName === "Vendor Management" && effectiveCompanyName === "WebiMax") return false;

          // Hide Power BI menu if user has no dashboards
          if (item.path === "/powerbi" && !hasDashboards) {
            return false;
          }

          // Existing Admins predate the employeeManagement permission key, so it's simply
          // absent from their stored permissions (not explicitly set to "no") -- treat that
          // as full access so nothing that used to be unconditionally visible disappears.
          if (
            item.permissionKey === "employeeManagement" &&
            user?.role === "Admin" &&
            permissions?.employeeManagement === undefined
          ) {
            return true;
          }

          // If route has a permissionKey, check if user has at least read access
          // If no permissionKey, always show (e.g., Profile)
          if (item.permissionKey) {
            return canView(permissions, item.permissionKey);
          }

          return true;
        })
        .map((item: CustomRoute) => {
          const isActive = !item.submenu?.length && item.path === activeFlatPath;
          const Icon = item.icon as React.ElementType | undefined;

          // Pre-filter submenu items if they exist
          let filteredSubmenu = item.submenu;
          if (item.submenu && item.submenu.length > 0) {
            filteredSubmenu = item.submenu.filter((sub) => {
              if (sub.hideInMenu) return false;
              if (sub.menuName === "Commission Approvals") return isCommissionApprover === true;
              if (shouldHideBillingOption(sub.path)) return false;

              if (
                (sub.path === "/netsuite/approvals") &&
                currentCompany?.invoicingAccounting === "QuickBooks" &&
                user?.role === "Admin"
              ) {
                return false;
              }

              if (user?.role === "Admin" && sub.path.includes("/reports/commission") && effectiveCompanyName !== "WebiMax") return false;
              if (sub.path.includes("vendor-bills") && effectiveCompanyName !== "WebiMax") return false;
              if (sub.path.includes("vendor-bill-approvals") && effectiveCompanyName !== "WebiMax") return false;
              if (sub.menuName === "Create Invoice" && effectiveCompanyName !== "WebiMax") return false;

              if (effectiveCompanyName !== "WebiMax" && sub.menuName === "Projects") return false;

              if (sub.crmFullOnly && getCrmAccess(user?.role, permissions) !== "full") return false;

              if (
                (sub.path === "/quickbooks/approvals") &&
                currentCompany?.invoicingAccounting === "NetSuite" &&
                user?.role === "Admin"
              ) {
                return false;
              }

              if (currentCompany?.invoicingAccounting === "QuickBooks" && user?.role === "Admin" && sub.path === "/billing/create-bill") {
                return false;
              }

              // Existing Admins predate the employeeManagement permission key, so it's simply
              // absent from their stored permissions (not explicitly set to "no") -- treat that
              // as full access so nothing that used to be unconditionally visible disappears.
              const isNewAdminEmployeeManagementDefault =
                sub.permissionKey === "employeeManagement" &&
                user?.role === "Admin" &&
                permissions?.employeeManagement === undefined;

              // Daily Reports is visible to OMS Sales admins too (view-only — see the dedicated
              // check below), so the generic "read access to permissionKey" gate below — which
              // would otherwise require read-or-full on oms_operations specifically — is
              // bypassed for this one path.
              const isDailyReportsPath = sub.path === "/oms/daily-reports" && sub.permissionKey === "oms_sales";

              // Check permissionKey for submenus
              if (
                sub.permissionKey &&
                !isNewAdminEmployeeManagementDefault &&
                !isDailyReportsPath &&
                !canView(permissions, sub.permissionKey)
              ) {
                return false;
              }

              // Hide "Create Invoice" for Employee if permission level is only "read"
              if (
                user?.role === "Employee" &&
                sub.path === "/my-invoices/create" &&
                sub.permissionKey === "BillingInvoices" &&
                !canEdit(permissions, sub.permissionKey)
              ) {
                return false;
              }

              // Hide "Create Bill" for Employee if permission level is only "read"
              if (
                user?.role === "Employee" &&
                sub.path === "/vendor-bills/create" &&
                sub.permissionKey === "BillingInvoices" &&
                !canEdit(permissions, sub.permissionKey)
              ) {
                return false;
              }

              // Hide "Permission Templates" unless the viewer has full (not just read) access
              // to Employee Management.
              if (
                sub.path === "/employee-management/permission-templates" &&
                sub.permissionKey === "employeeManagement" &&
                !isNewAdminEmployeeManagementDefault &&
                !canEdit(permissions, sub.permissionKey)
              ) {
                return false;
              }

              // Daily Reports (under OMS) is shown only to users whose permissions have
              // oms_sales === "full"; "no", "read", missing or anything else hides it.
              if (isDailyReportsPath && permissions?.oms_sales !== "full") {
                return false;
              }

              // Hide Vendor Bills Approvals from users who are not in bill approval config
              if (
                (sub.path === "/netsuite/vendor-bill-approvals" ||
                  sub.path === "/billing/vendor-bill-approvals" ||
                  sub.path === "/vendor-bill-approvals") &&
                (user?.role === "Finance" || user?.role === "Admin" || user?.role === "Employee")
              ) {
                return isApprover === true;
              }
              return true;
            });

            // If a menu option doesn't have any sub-menu items left, hide it completely
            if (filteredSubmenu.length === 0) {
              return null;
            }
          }

          // If the route has a submenu, render a collapsible dropdown
          if (filteredSubmenu && filteredSubmenu.length > 0) {
            const isOpen = !!openMenus[item.menuName];
            const activeSub = bestMatch(filteredSubmenu, pathname);
            // The section holds the current page: a visible child matches, or only a hidden detail/edit route does.
            const sectionActive = !!activeSub || !!item.submenu?.some((s) => s.hideInMenu && matches(s.path, pathname));
            return (
              <div key={item.menuName}>
                <button
                  type="button"
                  aria-expanded={isOpen}
                  className={cn(
                    "flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm rounded-lg transition-colors cursor-pointer",
                    sectionActive
                      ? "bg-current/10 font-semibold"
                      : isOpen
                        ? "bg-current/5 font-medium hover:bg-current/10"
                        : "font-medium hover:bg-current/5",
                  )}
                  style={{ color: textColor }}
                  onClick={() => toggleMenu(item.menuName)}
                >
                  <span className="flex items-center gap-3">
                    {Icon ? <Icon className="h-5 w-5" /> : null}
                    <span>{item.menuName}</span>
                  </span>

                  <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen ? "rotate-180" : "rotate-0")} />
                </button>

                <div
                  className={cn("mt-1 ml-5 space-y-1 border-l border-current/20 pl-3", isOpen ? "block" : "hidden")}
                  style={{ color: textColor }}
                >
                  {filteredSubmenu.map((sub) => {
                    const subActive = sub === activeSub;
                    return (
                      <Link
                        // menuName isn't unique (e.g. QuickBooks and NetSuite "Invoice Approvals").
                        key={`${sub.path}|${sub.menuName}`}
                        to={sub.path}
                        aria-current={subActive ? "page" : undefined}
                        className={cn(
                          "flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors",
                          subActive
                            ? cn(ACCENT_BAR, "before:-left-[13px] bg-current/15 font-medium opacity-100")
                            : "opacity-70 hover:bg-current/5 hover:opacity-100",
                        )}
                        style={{ color: textColor }}
                      >
                        {sub.menuName}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          }

          // Otherwise render a normal link
          return (
            <Link
              key={item.menuName}
              to={item.path}
              onClick={() => setOpenMenus({})}
              aria-current={isActive ? "page" : undefined}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors",
                isActive ? cn(ACCENT_BAR, "before:left-0 bg-current/15 font-semibold") : "font-medium hover:bg-current/5",
              )}
              style={{ color: textColor }}
            >
              {Icon ? <Icon className="h-5 w-5" /> : null}
              {item.menuName}
            </Link>
          );
        })}
    </div>
  );
};

export default UserMenu;
